import CloseIcon from "../../assets/img/closeIcon.svg";
import useTranslator from "../../hooks/useTranslator";
import {
  FiAlertTriangle,
  FiBriefcase,
  FiCheckCircle,
  FiClipboard,
  FiDollarSign,
  FiFileText,
  FiInfo,
  FiLoader,
  FiRefreshCw,
  FiShield,
  FiTrendingUp,
  FiUserPlus,
  FiXCircle,
} from "react-icons/fi";
import "./../../styles/risk-assessment-report-modal.css";
import LandLordContext from "../../context/landlord/LandLordContext";
import { useContext, useEffect, useState } from "react";
import { FaArrowRightLong } from "react-icons/fa6";
import { MdKeyboardArrowDown } from "react-icons/md";
import { HiOutlineBolt } from "react-icons/hi2";

const RiskAssessmentReport = (props) => {
  const { _t } = useTranslator();
  const landLordContext = useContext(LandLordContext);
  const {
    onClose,
    reportData,
    setEnabled,
    enabled,
    finalEffortRate,
    areAllSelectedTenantsInsurable,
    selectedTenants,
  } = props;
  const { getRiskAssessmentReport, sendAddGuarantorRecommendationEmail } =
    landLordContext;

  const [reportLoading, setReportLoading] = useState("loading");
  const [report, setReport] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isPending, setIsPending] = useState(false);

  const [currentLang, setCurrentLang] = useState(
    localStorage.getItem("i18nextLng").toLowerCase() || "en-us"
  );

  useEffect(() => {
    setCurrentLang(localStorage.getItem("i18nextLng").toLowerCase() || "en-us");
  }, [localStorage.getItem("i18nextLng")]);

  const reportDataCleaned = reportData?.report ? reportData : null;

  console.log("reportDataCleaned", reportDataCleaned);
  console.log("report", report);

  const loadReportData = () => {
    if (!reportDataCleaned) return;
    setReportLoading("loading");
    getRiskAssessmentReport(reportDataCleaned)
      .then((res) => {
        if (res?.data?.report) {
          setReport(res.data.report);
          setReportLoading("loaded");
        } else {
          setReportLoading("failed");
        }
      })
      .catch((error) => {
        console.log(error);
        setReportLoading("failed");
      });
  };

  const langChecker = currentLang === "en-us" ? "en" : "es";

  useEffect(() => {
    loadReportData();
  }, [reportData]);

  const applicants = reportData?.report?.applicants ?? [];
  const [activeTab, setActiveTab] = useState(applicants[0]?.name ?? "");
  const decisionRaw =
    report?.decision[langChecker] ??
    reportData?.report?.case?.decision ??
    "Conditional";

  const statusMeta = {
    Approved: { label: "Approved", className: "approved", Icon: FiCheckCircle },
    Conditional: {
      label: "Conditional",
      className: "conditional",
      Icon: FiAlertTriangle,
    },
    Rejected: { label: "Rejected", className: "rejected", Icon: FiXCircle },
  };

  const activeStatus = statusMeta[decisionRaw] ?? statusMeta.Conditional;
  const StatusIcon = activeStatus.Icon;

  const isNotApprovedState = activeStatus.label !== "Approved";

  const policyIconMap = {
    Affordability: <FiDollarSign size={15} />,
    "Employment Stability": <FiBriefcase size={15} />,
    "Financial behaviour": <FiTrendingUp size={15} />,
    "Financial Behaviour": <FiTrendingUp size={15} />,
    "Financial Behavior": <FiTrendingUp size={15} />,
    "Default Risk": <FiShield size={15} />,
    "Document Strength": <FiFileText size={15} />,
    "Credit History": <FiShield size={15} />,
    Documentation: <FiFileText size={15} />,
  };

  const policySubScoreMap = {
    affordability: [
      {
        label: { en: "Effort rate", es: "Tasa de esfuerzo" },
        key: "effortRate",
      },
      {
        label: { en: "Income source", es: "Fuente de ingresos" },
        key: "incomeVerification",
      },
      {
        label: { en: "Income consistency", es: "Consistencia de ingresos" },
        key: "incomeConsistency",
      },
    ],
    "employment stability": [
      {
        label: { en: "Employment tenure", es: "Antigüedad laboral" },
        key: "tenure",
      },
      {
        label: { en: "Employment Type", es: "Tipo de empleo" },
        key: "employmentPredictability",
      },
      {
        label: { en: "Employer Category", es: "Categoría del empleador" },
        key: "employerStability",
      },
      {
        label: { en: "Sector Cyclicality", es: "Ciclicidad del sector" },
        key: "sectorCyclicality",
      },
    ],
    "financial behaviour": [
      {
        label: { en: "Financial Score", es: "Puntuación financiera" },
        key: "financialSkor",
      },
    ],
    "financial behavior": [
      {
        label: { en: "Financial Score", es: "Puntuación financiera" },
        key: "financialSkor",
      },
    ],
    "default risk": [
      {
        label: { en: "Default history", es: "Historial de impagos" },
        key: "defaultHistory",
      },
    ],
    documentation: [
      {
        label: { en: "Document strength", es: "Solidez documental" },
        key: "documentation",
      },
    ],
  };

  const getRequirementKey = (requirement) =>
    requirement?.en?.toLowerCase() ?? "";

  const getScoreToneClass = (score) => {
    const numericScore = Number(score);
    if (Number.isNaN(numericScore)) return "partial";
    if (numericScore >= 75) return "yes";
    if (numericScore >= 50) return "partial";
    return "no";
  };

  const formatSubScoreValue = (subScore) => {
    const noDataLabel = langChecker === "es" ? "Sin datos" : "No data";

    if (!subScore) return noDataLabel;

    const label = subScore?.label;
    if (label) return label;

    return noDataLabel;
  };

  const getTone = (result = "") => {
    const lower = result?.en?.toLowerCase();
    if (lower === "strong" || lower === "acceptable") return "strong";
    if (lower === "weak") return "weak";
    return "medium";
  };

  // const handleCopy = () => {
  //     copy(JSON.stringify(reportData));
  //     alert(_t("copied"));
  // };

  const sendEmailToTenants = async () => {
    const tenantIds = selectedTenants.map((val) => val?.tenantid?._id);
    return Promise.allSettled(
      tenantIds.map((tenantId) =>
        sendAddGuarantorRecommendationEmail({
          tenantId,
        })
      )
    );
  };

  const handleRecommendation = async (action) => {
    setIsPending(true);
    try {
      if (action?.type === "insurance") {
        setEnabled((prev) => !prev);
      } else if (action?.type === "guarantor") {
        await sendEmailToTenants();
      }
      // #NOTE: handle other actions here
    } catch (err) {
      console.log(err);
    } finally {
      setIsPending(false);
    }
  };

  const renderContent = () => {
    if (!reportDataCleaned) {
      return (
        <div className="risk-report-state-container">
          <div className="risk-report-state-icon risk-report-state-icon--error">
            <FiClipboard size={32} />
          </div>
          <p className="risk-report-state-title">{_t("noReportDataFound")}</p>
          <p className="risk-report-state-desc">
            {_t("noApplicationDataWasProvidedToGenerateARiskAssessmentReport")}
          </p>
        </div>
      );
    }

    if (reportLoading === "loading") {
      return (
        <div className="risk-report-state-container">
          <div className="risk-report-state-icon risk-report-state-icon--spinning">
            <FiLoader color="#A3EB65" size={32} />
          </div>
          <p className="risk-report-state-title">{_t("generatingReport")}</p>
          <p className="risk-report-state-desc">
            {_t("wereAnalyzingTheApplicationThisMayTakeAFewSeconds")}
          </p>
        </div>
      );
    }

    if (reportLoading === "failed") {
      return (
        <div className="risk-report-state-container">
          <div className="risk-report-state-icon risk-report-state-icon--error">
            <FiXCircle size={32} />
          </div>
          <p className="risk-report-state-title">{_t("failedToLoadReport")}</p>
          <p className="risk-report-state-desc">
            {_t(
              "somethingWentWrongWhileGeneratingTheRiskAssessmentPleaseTryAgain"
            )}
          </p>
          <button className="risk-report-retry-btn" onClick={loadReportData}>
            <FiRefreshCw size={14} />
            {_t("retry")}
          </button>
        </div>
      );
    }

    return (
      <div className="risk-assessment-report-content">
        {/* ── Hero ── */}
        <section
          className={`risk-report-hero ${activeStatus.className} ${enabled ? "approved" : ""}`}
        >
          {/* Effort rate + insurable row */}
          <div className="d-flex align-items-center gap-8px">
            <div
              className={`risk-report-status-chip ${activeStatus.className} ${isNotApprovedState && enabled ? "strike" : ""}`}
            >
              <StatusIcon size={14} />
              <span>{_t(activeStatus.label.toLowerCase())}</span>
            </div>
            {isNotApprovedState && enabled && (
              <>
                <FaArrowRightLong size={12} style={{ color: "#94a3b8" }} />
                <div className="risk-report-status-chip approved">
                  <FiCheckCircle size={14} />
                  <span>{_t("approved")}</span>
                </div>
              </>
            )}
          </div>
          <div
            className="d-flex align-items-center gap-12px"
            style={{ marginBottom: 8 }}
          >
            <p className="risk-report-section-title effort-rate-title">
              <span>€ 600/mo</span>
            </p>
            <span className="dot-report" />
            <p className="risk-report-section-title effort-rate-title">
              {_t("effortRate")} — <span>{finalEffortRate}%</span>
            </p>
            <span className="dot-report" />
            <p
              className={`risk-report-section-title effort-rate-title badge-insurance d-flex align-items-center gap-8px ${areAllSelectedTenantsInsurable ? "insurable" : ""}`}
            >
              <img
                src={
                  areAllSelectedTenantsInsurable
                    ? "/images/shield.svg"
                    : "/images/not-insurable.svg"
                }
                alt=""
                style={{ width: 13 }}
              />
              {areAllSelectedTenantsInsurable ? (
                <span>{_t("insurable")}</span>
              ) : (
                <span style={{ color: "rgb(255, 60, 60)" }}>
                  {_t("notInsurable")}
                </span>
              )}
            </p>
          </div>

          {/* Decision + applicants row */}
          <div className="risk-report-hero-header">
            {/* <div>
                            <p className="risk-report-eyebrow">{_t("decision")}</p>
                            <div className="d-flex align-items-center gap-8px">
                                <div
                                    className={`risk-report-status-chip ${activeStatus.className} ${isNotApprovedState && enabled ? "strike" : ""}`}
                                >
                                    <StatusIcon size={14} />
                                    <span>{_t(activeStatus.label.toLowerCase())}</span>
                                </div>
                                {isNotApprovedState && enabled && (
                                    <>
                                        <FaArrowRightLong size={12} style={{ color: "#94a3b8" }} />
                                        <div className="risk-report-status-chip approved">
                                            <FiCheckCircle size={14} />
                                            <span>{_t("approved")}</span>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div> */}

            {/* {applicants.length > 0 && (
                            <div className="risk-report-applicants">
                                <p className="risk-report-eyebrow">{_t("tenantsEvaluated")}</p>
                                <div className="risk-report-applicant-list">
                                    {applicants.map((applicant) => (
                                        <div key={applicant.name} className="risk-report-applicant-chip">
                                            <FiUser size={12} />
                                            <span>{applicant.name}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )} */}
          </div>
          {report?.decisionSummary && (
            <p className="risk-report-summary-text">
              {report.decisionSummary[langChecker]?.split("\n")?.join(" ")}
            </p>
          )}
          <p className="risk-report-section-title recommended-action-title">
            {_t("recommendedActions")}
          </p>
          {/* ── Recommended Actions ── */}
          {report?.recommendedActions?.length > 0 && (
            <ul className="risk-report-action-list">
              {report.recommendedActions.map((action, idx) => (
                <>
                  {idx > 0 && (
                    <div className="risk-report-action-divider">
                      <span>{_t("or")}</span>
                    </div>
                  )}
                  <li
                    key={idx}
                    className={`risk-report-action-item ${action?.type === "insurance" ? "insurance cursor-pointer" : ""}`}
                    onClick={() => handleRecommendation(action)}
                    disabled={isPending}
                    style={{
                      cursor: isPending ? "not-allowed" : "pointer",
                      opacity: isPending ? 0.6 : 1,
                    }}
                  >
                    <div className="d-flex align-items-center gap-2">
                      {isPending ? (
                        <h4>{_t("submitting")}...</h4>
                      ) : (
                        <>
                          {action?.type === "insurance" ? (
                            <HiOutlineBolt
                              size={16}
                              style={{ color: "#fff" }}
                            />
                          ) : action?.type === "guarantor" ? (
                            <FiUserPlus
                              size={16}
                              style={{ color: "#253837" }}
                            />
                          ) : (
                            <FiInfo size={16} style={{ color: "#253837" }} />
                          )}
                          <h4>{action?.title[langChecker]}</h4>
                        </>
                      )}
                      {/* <span>{action?.info[langChecker]}</span> */}
                    </div>
                    {/* {action?.type === "insurance" ? (
                                                <div
                                                    className={`toggle-switch ${enabled ? "active" : ""}`}
                                                    onClick={() => setEnabled(!enabled)}
                                                />
                                            ) : ""} */}
                  </li>
                </>
              ))}
            </ul>
          )}
        </section>

        {/* ── Decision Summary ── */}
        {/* {report?.decisionSummary && (
                    <div className="risk-report-grid">
                        <section className="risk-report-section">
                            <p className="risk-report-section-title">{_t("decisionSummary")}</p>
                            <p className="risk-report-summary-text">
                                {report.decisionSummary[langChecker]?.split("\n")?.join(" ")}
                            </p>
                        </section>
                    </div>
                )} */}

        {/* Divider */}
        <div className="risk-report-divider" />

        {/* ── Policy Fit + Impact (side by side if both present) ── */}
        <div className="risk-report-grid">
          {/* Policy Fit Summary */}
          {report?.policyFitSummary?.length > 0 && (
            <section className="risk-report-section policy-fit-section">
              <p className="risk-report-section-title">
                {_t("policyFitSummary")}
              </p>
              <div className="d-flex align-items-center flex-wrap gap-8px tab-main-wrapper">
                {applicants?.map((applicant) => (
                  <span
                    key={applicant.name}
                    className={`user-tab ${activeTab === applicant.name ? "active-tab" : ""}`}
                    onClick={() => setActiveTab(applicant.name)}
                  >
                    {applicant.name}
                  </span>
                ))}
              </div>
              <div className="risk-report-policy-list">
                {report.policyFitSummary.map((item) => {
                  const requirementKey = getRequirementKey(item.requirement);
                  const subScoreItems = policySubScoreMap[requirementKey] ?? [];

                  return (
                    <div
                      key={requirementKey}
                      className="risk-report-policy-item flex-column"
                    >
                      <div
                        className="d-flex align-items-center justify-content-between gap-8px w-100 cursor-pointer"
                        onClick={() =>
                          setIsExpanded(
                            requirementKey === isExpanded
                              ? false
                              : requirementKey
                          )
                        }
                      >
                        <div className="risk-report-policy-label">
                          <span className="risk-report-policy-icon">
                            {policyIconMap[item.requirement["en"]] ?? (
                              <FiFileText size={15} />
                            )}
                          </span>
                          <span className="policy-title">
                            {item.requirement[langChecker]}
                          </span>
                        </div>
                        <div className="d-flex align-items-center gap-8px">
                          <span
                            className={`risk-report-tone ${getTone(item.result)}`}
                          >
                            {item.result[langChecker]}
                          </span>
                          <MdKeyboardArrowDown
                            size={16}
                            style={{
                              transform:
                                isExpanded === requirementKey
                                  ? "rotate(180deg)"
                                  : "rotate(0deg)",
                              transition: "transform 0.2s ease",
                              color: "#94a3b8",
                            }}
                            className="cursor-pointer"
                          />
                        </div>
                      </div>
                      {isExpanded === requirementKey && (
                        <div className="pf-expanded">
                          <div className="pf-cards-row">
                            {applicants.map((applicant) => (
                              <>
                                {activeTab === applicant.name ? (
                                  <>
                                    <div
                                      key={applicant.name}
                                      className="pf-card"
                                    >
                                      <div className="pf-card-header">
                                        <div
                                          className="pf-avatar"
                                          style={{
                                            background: applicant.avatarBg,
                                            color: applicant.avatarColor,
                                          }}
                                        >
                                          {applicant.name
                                            .split(" ")
                                            .map((n) => n[0])
                                            .join("")
                                            .slice(0, 2)
                                            .toUpperCase()}
                                        </div>
                                        <div>
                                          <div className="pf-name">
                                            {applicant.name}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="pf-rows">
                                        {subScoreItems.map((subScoreItem) => {
                                          const subScore =
                                            applicant?.subScores?.[
                                              subScoreItem.key
                                            ];

                                          return (
                                            <div
                                              className="pf-row"
                                              key={subScoreItem.key}
                                            >
                                              <span className="pf-label">
                                                {
                                                  subScoreItem.label[
                                                    langChecker
                                                  ]
                                                }
                                              </span>
                                              <span
                                                className={`pf-pill ${getScoreToneClass(subScore?.score)}`}
                                              >
                                                {formatSubScoreValue(subScore)}
                                              </span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  </>
                                ) : null}
                              </>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Impact */}
          {enabled && report?.impact && (
            <section className="risk-report-section">
              <p className="risk-report-section-title">
                {_t("ifRecommendationIsCompleted")}
              </p>
              <div className="risk-report-outcome-card">
                <div className="risk-report-outcome-row">
                  <span>{_t("projectedOutcome")}</span>
                  <strong className="risk-report-approved-text">
                    <FiCheckCircle size={14} />
                    {_t("approved")}
                  </strong>
                </div>
                <p className="risk-report-outcome-note">
                  {report.impact[langChecker]?.split("\n").join(" ")}
                </p>
              </div>
            </section>
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      className="risk-assessment-report-modal metermodal modal-xl wrapper-modal modalWidth_set"
      onClick={onClose}
    >
      <div
        className="doc-history-popup rental-overview-popup total-salary-modal share-profile-modal sharedTenantProfileModal open"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="doc-history-header linked-property-popup-padding">
          <div className="d-flex align-items-center custom-gap-2">
            <img
              src={CloseIcon}
              alt=""
              className="doc-history-close-btn"
              onClick={onClose}
            />
            <h4 className="custom-modal-heading">
              {_t("riskAssessmentReport")}
            </h4>
            {/* <button type="button" className="btn cancel-btn-report" onClick={handleCopy}>
                            <FaRegCopy />
                            &nbsp;{_t("copyData")}
                        </button> */}
          </div>
        </div>
        <div className="risk-report-divider" style={{ marginTop: 0 }} />
        <div className="rental-overview-scroll">{renderContent()}</div>
      </div>
    </div>
  );
};

export default RiskAssessmentReport;
